import React, { useState } from 'react';
import { Home, MessageCircle, Heart, User, Plus } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const MobileBottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('home');

  // Determine active tab based on current route
  React.useEffect(() => {
    const path = location.pathname;
    if (path === '/') setActiveTab('home');
    else if (path === '/chats' || path.includes('/chats')) setActiveTab('chats');
    else if (path === '/favorites') setActiveTab('favorites');
    else if (path === '/cart') setActiveTab('cart');
    else if (path === '/profile' || path === '/login' || path === '/register') setActiveTab('profile');
  }, [location.pathname]);

  const handleSellClick = () => {
    if (user) {
      navigate('/store');
    } else {
      navigate('/store', { state: { from: '/store' } });
    }
  };

  const navItems = [
    {
      id: 'home',
      label: 'Home',
      icon: Home,
      path: '/',
      activeColor: 'text-[#ff660e]',
    },
    {
      id: 'favorites',
      label: 'Saved',
      icon: Heart,
      path: '/favorites',
      activeColor: 'text-[#ff660e]',
      badge: user?.favoriteCount || 0,
    },
    {
      id: 'chats',
      label: 'Chats',
      icon: MessageCircle,
      path: '/chats',
      activeColor: 'text-[#ff660e]',
    },
    {
      id: 'profile',
      label: 'Profile',
      icon: User,
      path: user ? '/profile' : '/login',
      activeColor: 'text-[#ff660e]',
    },
  ];

  return (
    <>
      {/* Main Bottom Navigation */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 shadow-2xl z-40 overflow-visible">
        {/* Floating Action Button (Sell) - Positioned above the nav */}
        <div className="absolute left-1/2 -translate-x-1/2 -top-6 z-50">
          <button
            onClick={handleSellClick}
            className="w-14 h-14 bg-[#ff660e] rounded-full flex items-center justify-center shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95 transition-all"
            aria-label="Sell item"
          >
            <Plus className="w-6 h-6 text-white" />
          </button>
          <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 text-xs font-medium text-black whitespace-nowrap">
            Sell
          </span>
        </div>

        {/* Navigation Items Container */}
        <div className="flex justify-between items-center py-2 px-4 max-w-screen-sm mx-auto">
          {navItems.slice(0, 2).map((item) => { // Left side items (Home, Favorites)
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            
            return (
              <Link
                key={item.id}
                to={item.path}
                className="flex flex-col items-center flex-1 min-w-0 px-1"
                onClick={() => setActiveTab(item.id)}
              >
                {/* Icon Container */}
                <div className={`p-2 rounded-xl transition-all ${
                  isActive 
                    ? 'bg-[#ff660e]/10' 
                    : ''
                }`}>
                  <Icon className={`w-5 h-5 transition-colors ${
                    isActive ? item.activeColor : 'text-gray-500'
                  }`} />
                </div>
                
                {/* Label */}
                <span className={`text-xs transition-colors truncate mt-1 ${
                  isActive ? 'font-semibold text-black' : 'font-medium text-gray-500'
                }`}>
                  {item.label}
                </span>
                
                {/* Badge for Favorites */}
                {item.id === 'favorites' && item.badge > 0 && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-[#ff660e] text-white text-[10px] rounded-full flex items-center justify-center font-bold shadow-sm">
                    {item.badge > 9 ? '9+' : item.badge}
                  </div>
                )}
              </Link>
            );
          })}
          
          {/* Center Spacer for FAB */}
          <div className="w-20 flex-shrink-0"></div>
          
          {navItems.slice(2).map((item) => { // Right side items (Chats, Profile)
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            
            return (
              <Link
                key={item.id}
                to={item.path}
                className="flex flex-col items-center flex-1 min-w-0 px-1 relative"
                onClick={() => setActiveTab(item.id)}
              >
                {/* Icon Container */}
                <div className={`p-2 rounded-xl transition-all ${
                  isActive 
                    ? 'bg-[#ff660e]/10' 
                    : ''
                }`}>
                  <Icon className={`w-5 h-5 transition-colors ${
                    isActive ? item.activeColor : 'text-gray-500'
                  }`} />
                </div>
                
                {/* Label */}
                <span className={`text-xs transition-colors truncate mt-1 ${
                  isActive ? 'font-semibold text-black' : 'font-medium text-gray-500'
                }`}>
                  {item.label}
                </span>
              </Link>
            );
          })}
        </div>
        
        {/* Safe area spacer for iPhone notch */}
        <div className="h-safe-bottom bg-white"></div>
      </div>

      {/* Extra padding for content to avoid bottom nav overlap */}
      <div className="lg:hidden pb-20"></div>
    </>
  );
};

export default MobileBottomNav;